import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Request from '../../../../../../../helpers/request'
import * as url from '../../../../../../../helpers/url_helper'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../../../components/Common/PaginationBar'

const MODULE_TYPE_LABEL: Record<string, string> = {
    '0': '2Sw', '1': '4M', '2': 'Knob', '3': 'KnobPro',
    '4': '3R1F', '5': '4R', '98': 'Universal Plug', '99': 'Indian Plug',
}

// Format an ISO timestamp in IST for operator display (logs/DB are UTC).
const formatIST = (iso: string | null): string => {
    if (!iso) return ''
    try {
        return new Date(iso).toLocaleString('en-IN', {
            dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kolkata',
        }) + ' IST'
    } catch { return iso || '' }
}

type WizardStep = 'info' | 'reselect' | 'warn' | 'confirm' | null

interface Props {
    rooms: any[]
    pcbs: any[]
    relays: any[]
    placeId: string
    userId: string
    [key: string]: any
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const Overlay = ({ children }: { children: React.ReactNode }) => (
    <div
        data-testid="wizard-overlay"
        style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050,
        }}
    >
        <div style={{ background: '#fff', borderRadius: 10, padding: '32px 36px', width: 480, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            {children}
        </div>
    </div>
)

const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
        <span style={{ color: '#858D9D', fontSize: 13, width: 120 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{value}</span>
    </div>
)

const NetworkManagement = ({ rooms, pcbs, relays, placeId, userId }: Props) => {
    const [localPcbs, setLocalPcbs] = useState<any[] | null>(null)
    const [selectedModule, setSelectedModule] = useState<any>(null)
    const [step, setStep] = useState<WizardStep>(null)
    const [reselectedId, setReselectedId] = useState('')
    const [removing, setRemoving] = useState(false)
    const [page, setPage] = usePersistedPage(`place-network-${placeId}`)
    const [polling, setPolling] = useState<any>(null)
    const [pollingBusy, setPollingBusy] = useState(false)
    const [pauseDuration, setPauseDuration] = useState<string>('')

    const fetchPolling = () => {
        Request.post(url.POLLING_STATUS, {})
            .then((res: any) => setPolling(res?.data?.data || null))
            .catch(() => setPolling(null))
    }
    useEffect(() => { fetchPolling() }, [placeId])

    const placePolling = polling?.disabledPlaces?.find((p: any) => p.placeId === placeId) || null
    const placeAllow = polling?.allowedPlaces?.find((p: any) => p.placeId === placeId) || null
    const globalEnabled = !!(polling && polling.globalEnabled)
    const globallyPaused = !!(polling && polling.globalEnabled === false)
    const placePaused = !!placePolling
    const placeAllowed = !!placeAllow
    const pauseExpiresAt: string | null = placePolling?.expiresAt || null
    const allowExpiresAt: string | null = placeAllow?.expiresAt || null
    const dualListed = placePaused && placeAllowed

    const togglePlacePolling = () => {
        setPollingBusy(true)
        const body: any = { placeId, enabled: placePaused }
        if (!placePaused && pauseDuration) body.minutes = Number(pauseDuration)
        Request.post(url.POLLING_PLACE, body)
            .then((res: any) => {
                setPolling(res?.data?.data || null)
                toast.success(placePaused ? 'Polling resumed for this network' : 'Polling paused for this network')
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to update polling'))
            .finally(() => setPollingBusy(false))
    }

    const togglePlaceAllow = () => {
        setPollingBusy(true)
        const body: any = { placeId, enabled: !placeAllowed }
        if (!placeAllowed) body.minutes = Number(pauseDuration) || 60
        Request.post(url.POLLING_PLACE_ALLOW, body)
            .then((res: any) => {
                setPolling(res?.data?.data || null)
                toast.success(placeAllowed ? 'Test polling stopped for this network' : 'Test polling started for this network')
            })
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Failed to update test polling'))
            .finally(() => setPollingBusy(false))
    }

    const placeRooms = (rooms || []).filter(r => r.placeId === placeId)
    const roomMap = Object.fromEntries(placeRooms.map(r => [r.id, r.name]))
    const placeRelays = (relays || []).filter(r => r.placeId === placeId)
    const relayMap = Object.fromEntries(placeRelays.map(r => [r.id, r.name]))
    const placePcbs = localPcbs ?? (pcbs || []).filter(p => p.placeId === placeId)
    const { paged, totalPages, safePage } = paginate(placePcbs, page)

    const startWizard = (pcb: any) => {
        setSelectedModule(pcb)
        setStep('info')
        setReselectedId('')
    }

    const proceedFromReselect = () => {
        if (reselectedId !== selectedModule?.id) return
        const remaining = placePcbs.filter(p => p.id !== selectedModule.id)
        const isMaster = selectedModule.role === 'Master'
        const noBackup = remaining.every((p: any) => p.role !== 'Backup' && p.role !== 'Backup Master')
        setStep(isMaster && noBackup ? 'warn' : 'confirm')
    }

    const executeRemove = () => {
        setRemoving(true)
        Request.post(url.DELETE_CHIP, { chipId: selectedModule.id })
            .then((res: any) => {
                toast.success(res?.data?.msg || 'Module removed')
                setLocalPcbs(placePcbs.filter((p: any) => p.id !== selectedModule.id))
                setStep(null)
                setSelectedModule(null)
            })
            .catch(() => toast.error('Failed to remove module'))
            .finally(() => setRemoving(false))
    }

    const cancel = () => {
        setStep(null)
        setSelectedModule(null)
        setReselectedId('')
    }

    return (
        <div>
            <AutoUpdateStatus placeId={placeId} />
            {/* Polling kill-switch — per-network */}
            <div data-testid="polling-section" style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                <h6 style={{ margin: 0, fontWeight: 600 }}>Polling</h6>
                <p style={{ margin: '4px 0 12px', fontSize: 13, color: '#858D9D' }}>
                    Pause the backend's status-polling for this network while debugging. An optional duration auto-resumes.
                </p>
                {polling === null ? (
                    <span style={{ color: '#858D9D', fontSize: 13 }}>Polling status unavailable</span>
                ) : (
                    <>
                        {dualListed && (
                            <div
                                data-testid="polling-conflict-warning"
                                style={{ background: '#FFFAEB', border: '1px solid #FEDF89', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#B54708' }}
                            >
                                This network is on both the pause list and the allow list. Currently the {globalEnabled ? 'pause' : 'allow'} entry is in effect. Consider removing the other to avoid confusion.
                            </div>
                        )}
                        {globalEnabled && placePaused && (
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ color: '#B54708', fontSize: 14 }}>
                                    ● Paused {pauseExpiresAt ? `(auto-resumes ${formatIST(pauseExpiresAt)})` : '(indefinite)'}
                                </span>
                                <button
                                    data-testid="polling-resume-btn"
                                    onClick={togglePlacePolling}
                                    disabled={pollingBusy}
                                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #16A34A', background: '#fff', color: '#16A34A', cursor: pollingBusy ? 'not-allowed' : 'pointer', fontSize: 13 }}
                                >
                                    Resume polling
                                </button>
                            </div>
                        )}
                        {globalEnabled && !placePaused && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ color: '#16A34A', fontSize: 14 }}>● Polling active</span>
                                <select
                                    data-testid="polling-duration"
                                    value={pauseDuration}
                                    onChange={e => setPauseDuration(e.target.value)}
                                    style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                                >
                                    <option value="">Indefinite</option>
                                    <option value="15">15 min</option>
                                    <option value="60">1 hour</option>
                                    <option value="240">4 hours</option>
                                    <option value="1440">24 hours</option>
                                </select>
                                <button
                                    data-testid="polling-pause-btn"
                                    onClick={togglePlacePolling}
                                    disabled={pollingBusy}
                                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #D92D20', background: '#fff', color: '#D92D20', cursor: pollingBusy ? 'not-allowed' : 'pointer', fontSize: 13 }}
                                >
                                    Pause polling
                                </button>
                            </div>
                        )}
                        {globallyPaused && placeAllowed && (
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ color: '#16A34A', fontSize: 14 }}>
                                    ● Test polling active {allowExpiresAt ? `(auto-clears ${formatIST(allowExpiresAt)})` : '(indefinite)'}
                                </span>
                                <button
                                    data-testid="polling-stop-allow-btn"
                                    onClick={togglePlaceAllow}
                                    disabled={pollingBusy}
                                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #D92D20', background: '#fff', color: '#D92D20', cursor: pollingBusy ? 'not-allowed' : 'pointer', fontSize: 13 }}
                                >
                                    Stop test polling
                                </button>
                            </div>
                        )}
                        {globallyPaused && !placeAllowed && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ color: '#D92D20', fontSize: 14 }}>● All polling is paused globally</span>
                                <select
                                    data-testid="polling-allow-duration"
                                    value={pauseDuration || '60'}
                                    onChange={e => setPauseDuration(e.target.value)}
                                    style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #D0D5DD', borderRadius: 6 }}
                                >
                                    <option value="15">15 min</option>
                                    <option value="60">1 hour</option>
                                    <option value="240">4 hours</option>
                                    <option value="1440">24 hours</option>
                                </select>
                                <button
                                    data-testid="polling-start-allow-btn"
                                    onClick={togglePlaceAllow}
                                    disabled={pollingBusy}
                                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #16A34A', background: '#fff', color: '#16A34A', cursor: pollingBusy ? 'not-allowed' : 'pointer', fontSize: 13 }}
                                >
                                    Test polling on this network
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #E4E7EC' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>Modules</h6>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#858D9D' }}>
                        Destructive actions. Proceed with caution.
                    </p>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <TH>Panel</TH>
                            <TH>Type</TH>
                            <TH>MAC Address</TH>
                            <TH>Role</TH>
                            <TH>Room</TH>
                            <TH>Action</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {placePcbs.length === 0 && (
                            <tr>
                                <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No modules found
                                </td>
                            </tr>
                        )}
                        {paged.map((pcb: any, idx: number) => (
                            <tr key={pcb.id || idx}>
                                <TD>{relayMap[pcb.relayId] || '--'}</TD>
                                <TD>{MODULE_TYPE_LABEL[String(pcb.type)] ?? pcb.type ?? '--'}</TD>
                                <TD><code style={{ fontSize: 12 }}>{pcb.macAddress || '--'}</code></TD>
                                <TD>
                                    <span style={{ color: pcb.role === 'Master' ? '#D92D20' : pcb.role === 'Backup' ? '#B54708' : '#344054' }}>
                                        {pcb.role || '--'}
                                    </span>
                                </TD>
                                <TD>{pcb.roomId ? (roomMap[pcb.roomId] || pcb.roomId) : <span style={{ color: '#858D9D', fontStyle: 'italic' }}>Standalone</span>}</TD>
                                <TD>
                                    <button
                                        data-testid={`remove-btn-${pcb.id}`}
                                        onClick={() => startWizard(pcb)}
                                        style={{ background: 'none', border: '1px solid #D92D20', borderRadius: 4, color: '#D92D20', padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
                                    >
                                        Remove / Reset
                                    </button>
                                </TD>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <PaginationBar
                    page={safePage}
                    totalPages={totalPages}
                    total={placePcbs.length}
                    onPage={setPage}
                />
            </div>

            {/* ── Wizard overlay ─────────────────────────────────────────────── */}
            {step === 'info' && selectedModule && (
                <Overlay>
                    <h5 style={{ marginBottom: 20 }}>Module Selected</h5>
                    <InfoRow label="Name" value={selectedModule.name || '--'} />
                    <InfoRow label="Type" value={MODULE_TYPE_LABEL[String(selectedModule.type)] ?? selectedModule.type ?? '--'} />
                    <InfoRow label="MAC" value={selectedModule.macAddress || '--'} />
                    <InfoRow label="Role" value={selectedModule.role || '--'} />
                    <InfoRow label="Room" value={selectedModule.roomId ? (roomMap[selectedModule.roomId] || selectedModule.roomId) : 'Standalone'} />
                    <div style={{ display: 'flex', gap: 12, marginTop: 28, justifyContent: 'flex-end' }}>
                        <button onClick={cancel} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #D0D5DD', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                        <button onClick={() => setStep('reselect')} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#344054', color: '#fff', cursor: 'pointer' }}>
                            Continue
                        </button>
                    </div>
                </Overlay>
            )}

            {step === 'reselect' && (
                <Overlay>
                    <h5 style={{ marginBottom: 8 }}>Confirm Module Selection</h5>
                    <p style={{ fontSize: 13, color: '#858D9D', marginBottom: 20 }}>
                        Select the module you want to remove from the dropdown below to confirm your intent.
                    </p>
                    <select
                        data-testid="reselect-dropdown"
                        value={reselectedId}
                        onChange={e => setReselectedId(e.target.value)}
                        className="form-control"
                        style={{ marginBottom: 20 }}
                    >
                        <option value="">-- Select module to confirm --</option>
                        {placePcbs.map((pcb: any) => (
                            <option key={pcb.id} value={pcb.id}>
                                {pcb.name || pcb.macAddress || pcb.id} ({MODULE_TYPE_LABEL[String(pcb.type)] ?? pcb.type})
                            </option>
                        ))}
                    </select>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        <button onClick={cancel} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #D0D5DD', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                        <button
                            data-testid="proceed-btn"
                            onClick={proceedFromReselect}
                            disabled={reselectedId !== selectedModule?.id}
                            style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: reselectedId === selectedModule?.id ? '#D92D20' : '#ccc', color: '#fff', cursor: reselectedId === selectedModule?.id ? 'pointer' : 'not-allowed' }}
                        >
                            Proceed
                        </button>
                    </div>
                </Overlay>
            )}

            {step === 'warn' && (
                <Overlay>
                    <div style={{ background: '#FEF3F2', border: '1px solid #FDA29B', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
                        <h6 style={{ color: '#B42318', margin: '0 0 8px' }}>⚠ Master Module Warning</h6>
                        <p style={{ color: '#B42318', fontSize: 13, margin: 0 }}>
                            This module is the <strong>network master</strong>. No backup master is currently assigned.
                            Removing it may disconnect all devices in this place until a new master is elected or assigned via the App.
                        </p>
                    </div>
                    <p style={{ fontSize: 13, color: '#344054' }}>
                        Assign a backup master via the Dayro App before proceeding, or acknowledge the risk below.
                    </p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        <button onClick={cancel} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #D0D5DD', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                        <button
                            data-testid="acknowledge-btn"
                            onClick={() => setStep('confirm')}
                            style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #D92D20', background: '#fff', color: '#D92D20', cursor: 'pointer' }}
                        >
                            I understand, continue
                        </button>
                    </div>
                </Overlay>
            )}

            {step === 'confirm' && (
                <Overlay>
                    <h5 style={{ marginBottom: 8, color: '#D92D20' }}>Remove / Reset Module</h5>
                    <p style={{ fontSize: 13, color: '#344054', marginBottom: 4 }}>
                        You are about to remove:
                    </p>
                    <div style={{ background: '#F9FAFB', border: '1px solid #E4E7EC', borderRadius: 6, padding: '12px 16px', marginBottom: 20 }}>
                        <strong>{selectedModule?.name || '--'}</strong>
                        <span style={{ color: '#858D9D', marginLeft: 8, fontSize: 13 }}>
                            {MODULE_TYPE_LABEL[String(selectedModule?.type)] ?? selectedModule?.type}
                            {selectedModule?.macAddress ? ` · ${selectedModule.macAddress}` : ''}
                        </span>
                    </div>
                    <p style={{ fontSize: 12, color: '#858D9D' }}>This action cannot be undone. A log entry will be recorded.</p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        <button onClick={cancel} style={{ padding: '8px 20px', borderRadius: 6, border: '1px solid #D0D5DD', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                        <button
                            data-testid="confirm-remove-btn"
                            onClick={executeRemove}
                            disabled={removing}
                            style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#D92D20', color: '#fff', cursor: 'pointer' }}
                        >
                            {removing ? 'Removing…' : 'Remove Module'}
                        </button>
                    </div>
                </Overlay>
            )}
        </div>
    )
}

// ── Auto-update (sequenced OTA engine) status — Ram 2026-08-08 ─────────────
// Read-only view of this network's OTA position: per-module pipeline stage +
// latest job verdict, so a stuck network is VISIBLE, not buried in logs.
const AutoUpdateStatus = ({ placeId }: { placeId: string }) => {
    const [snap, setSnap] = React.useState<any>(null)
    const [busy, setBusy] = React.useState(false)
    const [err, setErr] = React.useState<string | null>(null)

    const fetchSnap = () => {
        setBusy(true)
        setErr(null)
        Request.post(url.OTA_PLACE_STATUS, { placeId })
            .then((res: any) => setSnap(res?.data?.data || null))
            .catch((e: any) => {
                setSnap(null)
                // Honest failure states (engine review 2026-08-08): a 404
                // means the backend predates the endpoint — say that, never
                // render a blank affirmative.
                setErr(e?.response?.status === 404
                    ? 'This backend version does not expose the OTA engine status yet.'
                    : 'Failed to load auto-update status — use Refresh to retry.')
            })
            .finally(() => setBusy(false))
    }
    React.useEffect(() => { fetchSnap() }, [placeId])

    const rows = (snap?.modules || []).filter((m: any) =>
        m.pipeline || m.job || m.sentOTAVersion)
    const issue = (m: any) => m.job && ['failed', 'straggler'].includes(m.job.state)

    return (
        <div data-testid="auto-update-status" style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h6 style={{ margin: 0, fontWeight: 600 }}>Auto-update (OTA engine)</h6>
                <span style={{ fontSize: 12, color: err ? '#B54708' : '#858D9D' }}>
                    {err ? err : (<>
                    mode <b>{snap?.mode ?? '…'}</b>
                    {snap?.lastEngineRunAt ? ` · last engine run ${new Date(snap.lastEngineRunAt).toLocaleString('en-IN')}` : ' · engine has not run this network yet'}
                    </>)}
                </span>
                <button onClick={fetchSnap} disabled={busy}
                    style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: 12, background: '#fff', border: '1px solid #D1D5DB', borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer' }}>
                    {busy ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>
            {err ? null : rows.length === 0 ? (
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#858D9D' }}>
                    No OTA activity recorded for this network — every module is either current or has never been offered an update.
                </p>
            ) : (
                <table style={{ width: '100%', marginTop: 10, fontSize: 12.5, borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ color: '#858D9D', textAlign: 'left' }}>
                            <th style={{ padding: '4px 8px' }}>MAC</th>
                            <th style={{ padding: '4px 8px' }}>Unicast</th>
                            <th style={{ padding: '4px 8px' }}>FW</th>
                            <th style={{ padding: '4px 8px' }}>Pipeline</th>
                            <th style={{ padding: '4px 8px' }}>Job</th>
                            <th style={{ padding: '4px 8px' }}>Attempts</th>
                            <th style={{ padding: '4px 8px' }}>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((m: any, i: number) => (
                            <tr key={m.macAddress || `row-${i}`} style={{ borderTop: '1px solid #F3F4F6', background: issue(m) ? '#FFFAFA' : undefined }}>
                                <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{m.macAddress}</td>
                                <td style={{ padding: '4px 8px' }}>{m.unicastAddress > 0 ? m.unicastAddress : '—'}</td>
                                <td style={{ padding: '4px 8px' }}>{m.firmwareVersion || '—'}</td>
                                <td style={{ padding: '4px 8px' }}>{m.pipeline || '—'}</td>
                                <td style={{ padding: '4px 8px', color: issue(m) ? '#B42318' : (m.job?.state === 'success' ? '#027A48' : '#475467'), fontWeight: issue(m) ? 600 : 400 }}>
                                    {m.job ? `${m.job.state}${m.job.target ? ' → ' + m.job.target : ''}` : '—'}
                                </td>
                                <td style={{ padding: '4px 8px' }}>{m.job ? `${m.job.attempts ?? 0} (pings ${m.job.pingAcks}/${m.job.pingsSent})` : '—'}</td>
                                <td style={{ padding: '4px 8px', color: '#858D9D' }}>{m.job?.note || ''}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    )
}

export default NetworkManagement
